import CustomDialog from 'Components/CustomDialog/CustomDialog'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import type { AffiliateTicketOrderLineShape } from 'Services/SalesManagement/Features/Order/orderSlice'
import type { AffiliateOrderRefundOptions } from 'Services/SalesManagement/Features/Order/types'
import useTypedSelector from 'Store'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField
} from '@mui/material'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { type FormEvent, useEffect, useState } from 'react'
import { t } from 'ttag'

interface MobileOrderRefundDialogProps {
  orderLine?: AffiliateTicketOrderLineShape
  open: boolean
  mobileOrderRefundOptions: { field_choices: AffiliateOrderRefundOptions } | undefined
  isRefundOptionsLoading: boolean
  handleClose: () => void
  handleSubmit: (reason: string, note: string, refundAmount?: number) => void
}

export default function MobileOrderRefundDialog({
  orderLine,
  open,
  mobileOrderRefundOptions,
  isRefundOptionsLoading,
  handleClose,
  handleSubmit
}: Readonly<MobileOrderRefundDialogProps>) {
  const [refundReason, setRefundReason] = useState('')
  const [refundNote, setRefundNote] = useState('')
  const [refundAmount, setRefundAmount] = useState('')

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress
  useEffect(() => {
    setRefundReason('')
    setRefundNote('')
    setRefundAmount('')
  }, [open])

  const { refundLoading } = useTypedSelector(state => state.order)

  const refundReasons = mobileOrderRefundOptions?.field_choices?.reason || []

  const totalRefunded = orderLine?.refund_summary.refunded_total ?? 0
  const availableRefundable = orderLine?.refund_summary.refundable_amount ?? 0

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    setRefundReason(value)
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const parsed = Number.parseFloat(refundAmount)
    const parsedAmount = Number.isFinite(parsed) ? parsed : undefined
    handleSubmit(refundReason, refundNote, parsedAmount)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefundNote(event.target.value)
  }

  const isRefundLoading = refundLoading === 'pending'

  return (
    <CustomDialog open={open} handleClose={handleClose}>
      <DialogTitle id="dialog-title">{t`Request refund`}</DialogTitle>
      <StyledForm onSubmit={onSubmit} isDisabled={isRefundLoading}>
        <WithLoader loading={isRefundOptionsLoading} rows={4}>
          <DialogContent>
            <Box>
              <DialogContentText sx={{ pb: 1 }}>
                {orderLine ? (
                  <>
                    {t`You are about to request a refund for ticket:`} <br />
                    <b>
                      {orderLine.ticket.product_name} ({orderLine.ticket.ticket_id})
                    </b>
                    <br />
                    {t`Total refunded`}: <b>{totalRefunded.toFixed(2)} €</b>
                    <br />
                    {t`Remaining refundable amount`}: <b>{availableRefundable.toFixed(2)} €</b>
                  </>
                ) : (
                  t`You are about to request a refund for the whole order.`
                )}
              </DialogContentText>
              <FormControl sx={{ minWidth: 200 }} size="small" required>
                <InputLabel>{t`Refund reason`}</InputLabel>
                <Select
                  required
                  value={refundReason}
                  label={t`Refund reason`}
                  autoWidth
                  onChange={handleSelectChange}
                >
                  {refundReasons.map((option: [number, string]) => (
                    <MenuItem key={option[0]} value={option[0]}>
                      {option[1]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                sx={{ width: '100%' }}
                required
                label={t`Note`}
                multiline
                rows={3}
                value={refundNote}
                onChange={handleInputChange}
              />
              {orderLine && (
                <TextField
                  sx={{ width: '100%' }}
                  label={t`Refund amount`}
                  type="number"
                  slotProps={{
                    htmlInput: {
                      min: 0.01,
                      max: availableRefundable,
                      step: 0.01
                    }
                  }}
                  value={refundAmount}
                  onChange={e => setRefundAmount(e.target.value)}
                />
              )}
            </Box>
          </DialogContent>
        </WithLoader>
        <DialogActions>
          <Button
            sx={{ color: 'text.primary', marginTop: 2 }}
            onClick={handleClose}
          >{t`Cancel`}</Button>
          <CustomLoadingButton
            isLoading={isRefundLoading}
            type="submit"
            size="medium"
            iconPosition="end"
            autoFocus
          >
            {orderLine ? t`Refund ticket` : t`Refund order`}
          </CustomLoadingButton>
        </DialogActions>
      </StyledForm>
    </CustomDialog>
  )
}
